/**
 * Shared pure-math scene-geometry builder for WIPEOUT (DEFUSE Direction-3).
 *
 * Single one-truth the legacy `../wipeoutToRenderable.ts` (reads the
 * `EntityPreview.properties` string bag) and the typed
 * `../typed/wipeoutFromEntity.ts` (reads the canonical `WipeoutEntity`
 * `vertices` field) both call. Both paths differ ONLY in how they extract
 * the raw `Vec3[]` corners; the geometry build + the collinearity reject +
 * the AABB are identical, so they live here once (no typed↔legacy clone).
 *
 * AutoCAD's WIPEOUT is a closed polygon with an opaque white fill that masks
 * geometry beneath it (DXF `AcDbWipeout` extends `AcDbRasterImage` — see
 * drawing-model `wipeoutEntitySchema`). Our renderer treats it as a flat
 * `THREE.Mesh` with a white `MeshBasicMaterial`.
 *
 * # Behaviour (golden-parity quirks reproduced exactly)
 *
 *   - Fewer than 3 corners → `null` (renderer skips). The legacy adapter
 *     reads `"Vertex count"` from the bag and bails on `< 3` BEFORE the loop;
 *     here we equivalently bail on `cornersRaw.length < 3`.
 *   - `applyOffset` is applied to every corner (identical to the legacy path
 *     which calls `applyOffset({ x, y, z: 0 })` per vertex).
 *   - Collinear / zero-area contour → `null`. The legacy `signedArea` check
 *     (`area < 1e-9`) rejects slivers so the renderer's earcut never produces
 *     a degenerate (invisible) mesh. Same threshold, same `Math.abs`.
 *   - z is always 0: the legacy bag drops z (`buildWipeoutPreview` emits only
 *     `Vertex N X` / `Vertex N Y`) AND the model's `vertices` are 2D
 *     (`drawingPointSchema` has no z). Callers therefore feed `z: 0` corners —
 *     there is NO z-superset divergence for this kind.
 *
 * Pure: no `EntityPreview` / `DrawingEntity` import, no side effects beyond
 * reading the locked-in coordinate origin via `applyOffset`.
 */

import { applyOffset } from "../../coordinateOrigin";
import type { SceneGeometry, Vec3 } from "../../types";

import { boundsFromPoints } from "../adapterContext";
import type { SceneGeometryResult } from "../sceneGeometryBuilders";

/**
 * Build the `wipeout` geometry + AABB from the raw (pre-offset) boundary
 * corners. `applyOffset` is applied to every corner. Returns `null` when the
 * contour cannot paint an interior — fewer than 3 corners, or a collinear /
 * zero-area set — matching the legacy adapter's reject paths exactly.
 */
export function buildWipeoutSceneGeometry(
  cornersRaw: Vec3[],
): SceneGeometryResult | null {
  // Legacy bails on a vertex count `< 3` (read from the bag) before assembling
  // any corners. Equivalent reject on the already-extracted corner list.
  if (cornersRaw.length < 3) {
    return null;
  }

  const corners = cornersRaw.map((c) => applyOffset(c));

  // Collinearity check — if every corner sits on a single line the wipeout has
  // no interior to paint. `signedArea` is twice the polygon area for a flat 2D
  // contour; values near zero indicate a sliver / collinear set.
  const area = Math.abs(signedArea(corners));
  if (area < 1e-9) {
    return null;
  }

  const geometry: SceneGeometry = { kind: "wipeout", corners };
  const bounds = boundsFromPoints(corners);
  return { geometry, bounds };
}

/** Twice the signed area of the flat 2D contour (shoelace formula). */
function signedArea(points: Vec3[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    if (!a || !b) continue;
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}
